import React, { useState, useEffect } from 'react';
import {
  Space,
  Tabs,
  Card,
  Button,
  Typography,
  Table,
  Tag,
  Spin,
  Row,
  Col,
  Empty,
} from 'antd';
import {
  PlusOutlined,
  UnorderedListOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import LeaveRequestList from './LeaveRequestList';
import LeaveRequestForm from '../calendar/LeaveRequestForm';
import { leaveService } from '../../services/leaveService';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import notify from '../../utils/notify';
import getApiErrorMessage from '../../utils/getApiErrorMessage';
import ViewModeSelect from '../common/ViewModeSelect';
import useResponsiveTableView from '../../hooks/useResponsiveTableView';

const { Title, Text } = Typography;

// Four narrow balance columns; below this the card sits in a narrow column → cards.
const MIN_TABLE_WIDTH = 520;

const LEAVE_TYPE_COLORS = {
  VACATION: 'blue',
  SICK: 'red',
  MATERNITY: 'pink',
  PATERNITY: 'cyan',
  BEREAVEMENT: 'default',
  MARRIAGE: 'purple',
};

const LeaveManagement = () => {
  const [activeTab, setActiveTab] = useState('1');
  const [formOpen, setFormOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editingRequest, setEditingRequest] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);
  const [balances, setBalances] = useState([]);
  const [balancesLoading, setBalancesLoading] = useState(false);
  const [manualView, setManualView] = useState('auto');
  const { containerRef, view } = useResponsiveTableView({ minTableWidth: MIN_TABLE_WIDTH, manualView });
  const { user, canApproveLeaveRequests } = useAuth();
  const { t } = useTranslation();
  const canApproveRequests =
    canApproveLeaveRequests?.() ||
    user?.roles?.some((role) => ['ROLE_ADMIN', 'ROLE_HR', 'ADMIN', 'HR'].includes(role));

  useEffect(() => {
    if (!canApproveRequests && user?.employeeId) {
      setBalancesLoading(true);
      leaveService
        .getEmployeeBalances(user.employeeId)
        .then((data) => setBalances(data))
        .catch(() => notify.error(t('leave.myBalances.fetchError')))
        .finally(() => setBalancesLoading(false));
    }
  }, [user?.employeeId, canApproveRequests, t]);

  const balanceColumns = [
    {
      title: t('employee.balance.leaveType'),
      dataIndex: 'leaveType',
      key: 'leaveType',
      render: (type) => (
        <Tag color={LEAVE_TYPE_COLORS[type] || 'default'}>
          {t(`leave.form.types.${type}`)}
        </Tag>
      ),
    },
    {
      title: t('employee.balance.remainingDays'),
      dataIndex: 'balance',
      key: 'balance',
      render: (val) => parseFloat(val).toFixed(1),
    },
    {
      title: t('leave.myBalances.carryOver'),
      dataIndex: 'carriedOverDays',
      key: 'carriedOverDays',
      render: (val) => (val != null ? parseFloat(val).toFixed(1) : '—'),
    },
    {
      title: t('employee.balance.year'),
      dataIndex: 'year',
      key: 'year',
    },
  ];

  const renderBalanceCards = () => {
    if (!balancesLoading && balances.length === 0) {
      return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />;
    }
    return (
      <Row gutter={[12, 12]}>
        {balances.map((balance) => (
          <Col xs={24} sm={12} lg={8} key={balance.id}>
            <Card size="small" data-testid="balance-card">
              <Space direction="vertical" size={4} style={{ width: '100%' }}>
                <Tag color={LEAVE_TYPE_COLORS[balance.leaveType] || 'default'}>
                  {t(`leave.form.types.${balance.leaveType}`)}
                </Tag>
                <Text>{`${t('employee.balance.remainingDays')}: ${parseFloat(balance.balance).toFixed(1)}`}</Text>
                <Text type="secondary">
                  {`${t('leave.myBalances.carryOver')}: ${balance.carriedOverDays != null ? parseFloat(balance.carriedOverDays).toFixed(1) : '—'}`}
                </Text>
                <Text type="secondary">{`${t('employee.balance.year')}: ${balance.year}`}</Text>
              </Space>
            </Card>
          </Col>
        ))}
      </Row>
    );
  };

  const handleOpenCreate = () => {
    setEditingRequest(null);
    setFormOpen(true);
  };

  const handleEdit = (record) => {
    setEditingRequest(record);
    setFormOpen(true);
  };

  const handleFormClose = () => {
    setFormOpen(false);
    setEditingRequest(null);
  };

  const handleFormSubmit = async (requestData) => {
    try {
      setLoading(true);
      if (editingRequest) {
        await leaveService.updateRequest(editingRequest.id, requestData);
        notify.success(t('leave.editSuccess'));
      } else {
        await leaveService.createRequest(requestData);
        notify.success(t('leave.createSuccess'));
      }
      setFormOpen(false);
      setEditingRequest(null);
      setRefreshTick((t) => t + 1);
    } catch (error) {
      const errorKey = editingRequest ? 'leave.editError' : 'leave.createError';
      console.error('Error saving leave request:', error);
      notify.error(getApiErrorMessage(error, errorKey, t));
    } finally {
      setLoading(false);
    }
  };

  const tabItems = [
    {
      key: '1',
      label: (
        <span>
          {canApproveRequests ? (
            <>
              <ClockCircleOutlined />
              {t('leave.pendingRequests')}
            </>
          ) : (
            <>
              <UnorderedListOutlined />
              {t('leave.myRequests')}
            </>
          )}
        </span>
      ),
      children: <LeaveRequestList key={`${activeTab}-${refreshTick}`} onEdit={handleEdit} />,
    },
  ];

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {!canApproveRequests && (
        <Card
          title={t('leave.myBalances.title')}
          extra={<ViewModeSelect value={manualView} onChange={setManualView} data-testid="view-mode-select" />}
        >
          <div ref={containerRef}>
            <Spin spinning={balancesLoading}>
              {view === 'table' ? (
                <Table
                  columns={balanceColumns}
                  dataSource={balances}
                  rowKey="id"
                  pagination={false}
                  size="small"
                  data-testid="my-balances-table"
                />
              ) : (
                renderBalanceCards()
              )}
            </Spin>
          </div>
        </Card>
      )}
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 24 }}>
          <Title level={2} style={{ margin: 0 }}>
            {t('leave.title')}
          </Title>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleOpenCreate}
            size="large"
          >
            {t('leave.requestTimeOff')}
          </Button>
        </div>

        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
        />
      </Card>

      <LeaveRequestForm
        open={formOpen}
        onClose={handleFormClose}
        onSubmit={handleFormSubmit}
        loading={loading}
        editingRequest={editingRequest}
      />
    </Space>
  );
};

export default LeaveManagement;
